/**
 * ป้าย Kanban การผลิต (`OUTPUT_TAG`) — รูปร่างข้อมูลตาม
 * `apps/api/src/cap_erp_api/modules/production_tag/output_tag_router.py` (D-169, D-170)
 *
 * ไฟล์นี้เป็นภาพสะท้อนของ response จริงเท่านั้น ไม่ใช่ที่ตัดสินใจเพิ่มเติมเอง —
 * เหมือน `lib/productionOrders/types.ts`
 *
 * ⚠️ `qty_per_pkg` เดินทางเป็น **string** เสมอ (`AmountOut` ฝั่งเซิร์ฟเวอร์ serialize
 * จาก `Decimal`) ห้ามแปลงผ่าน `Number`/`parseFloat`
 */

export const OUTPUT_TAG_STATUSES = ['ACTIVE', 'REVOKED', 'REPRINTED', 'CONSUMED'] as const;
export type OutputTagStatus = (typeof OUTPUT_TAG_STATUSES)[number];

export const OUTPUT_TAG_STAGES = ['WIP', 'FG'] as const;
export type OutputTagStage = (typeof OUTPUT_TAG_STAGES)[number];

/** `422` ตอนพิมพ์ป้ายเมื่อสินค้ายังไม่มี packing standard ที่ใช้งานอยู่ */
export const NO_ACTIVE_PACKING_STANDARD = 'NO_ACTIVE_PACKING_STANDARD';
/** `409` ตอนพิมพ์ป้ายเมื่อใบสั่งผลิตยังไม่ถูกสั่งปล่อย */
export const PRODUCTION_ORDER_NOT_RELEASED = 'PRODUCTION_ORDER_NOT_RELEASED';
/** `409` ตอนพิมพ์ป้ายเมื่อ operation นั้นมีป้าย ACTIVE อยู่แล้ว (ให้พิมพ์ซ้ำแทน) */
export const OUTPUT_TAG_ALREADY_ACTIVE = 'OUTPUT_TAG_ALREADY_ACTIVE';
/** `409` ตอนพิมพ์ซ้ำเมื่อป้ายเดิมไม่ใช่ ACTIVE แล้ว */
export const OUTPUT_TAG_NOT_ACTIVE = 'OUTPUT_TAG_NOT_ACTIVE';
/** `422` ตอนพิมพ์ป้ายเมื่อจำนวนแพ็คเกจเกิน 500 (D-170 ข้อ 4) */
export const TOO_MANY_PACKAGES = 'TOO_MANY_PACKAGES';

/** หนึ่งขั้นของ routing บนป้าย — ขั้นที่ป้ายใบนี้อยู่ถูกเน้นด้วย `current` */
export interface OutputTagProcessFlowStep {
  readonly operation_seq: number;
  readonly work_center_code: string | null;
  readonly work_center_name: string | null;
  readonly current: boolean;
}

/**
 * สิ่งที่พิมพ์จริง — เก็บไว้เพื่อให้พิมพ์ซ้ำได้เหมือนเดิมแม้ master จะเปลี่ยนไปแล้ว
 * (D-170 ข้อ 3) จึงเป็นแหล่งข้อมูลเดียวที่ `KanbanTagSheet` อ่าน ไม่คำนวณใหม่จากที่อื่น
 *
 * ทุกฟิลด์ nullable เพราะ `print_data` ฝั่งเซิร์ฟเวอร์เป็น `Dict[str, Any]` ที่ไม่มี
 * schema บังคับ — parser ที่นี่ตรวจเฉพาะฟิลด์ที่ป้ายใช้จริงเท่านั้น (D-170 invariant)
 */
export interface OutputTagPrintData {
  readonly plant_code: string | null;
  readonly order_no: string | null;
  readonly operation_seq: number | null;
  readonly part_no: string | null;
  readonly part_name: string | null;
  readonly model: string | null;
  readonly planning_code: string | null;
  readonly tag_color: string | null;
  readonly line: string | null;
  readonly line_name: string | null;
  readonly process_flow: readonly OutputTagProcessFlowStep[];
  /** `NUMERIC` — string เสมอ */
  readonly qty_per_pkg: string | null;
  readonly qty_uom: string | null;
  readonly pkg_type: string | null;
  /** หนึ่งป้ายต่อหนึ่งแพ็คเกจ (D-170 ข้อ 4) — null คือ print_data เก่าก่อนแก้ */
  readonly package_seq: number | null;
  readonly package_count: number | null;
  /** `NUMERIC` — จำนวนในแพ็คเกจนี้ (ป้ายสุดท้ายถือเศษ) — null คือ print_data เก่าก่อนแก้ */
  readonly tag_qty: string | null;
  readonly stage: string | null;
  readonly store_location: string | null;
  readonly process_leadtime: string | null;
  readonly process_leadtime_basis: string | null;
  readonly image_id: string | null;
  readonly tag_code: string | null;
  readonly lot_no: string | null;
  readonly qr: string | null;
  readonly printed_at: string | null;
  readonly reprint_of: string | null;
}

/**
 * ป้ายหนึ่งใบ (หนึ่งแพ็คเกจ) ตามที่เซิร์ฟเวอร์ตอบ — `GET`/`reprint` คืนป้ายเดี่ยวรูปนี้;
 * `POST` คืนรายการของรูปนี้ หนึ่งรายการต่อแพ็คเกจที่พิมพ์ (D-170 ข้อ 4)
 */
export interface OutputTag {
  readonly tag_code: string;
  readonly status: string;
  readonly stage: string;
  readonly plant_code: string | null;
  readonly production_order_id: string;
  readonly operation_seq: number;
  readonly lot_no: string;
  readonly lot_id: string | null;
  readonly replaced_by_tag_code: string | null;
  /** `NUMERIC` — string เสมอ */
  readonly qty_per_pkg: string | null;
  /** หนึ่งป้ายต่อหนึ่งแพ็คเกจ (D-170 ข้อ 4) — ไม่ null เพราะเซิร์ฟเวอร์ตอบเสมอ */
  readonly package_seq: number;
  readonly package_count: number;
  /** `NUMERIC` — จำนวนในแพ็คเกจนี้ (ป้ายสุดท้ายถือเศษ) — string เสมอ */
  readonly tag_qty: string;
  readonly printed_by_employee_id: string;
  readonly printed_at: string;
  readonly print_data: OutputTagPrintData;
}

export interface OutputTagListPage {
  readonly items: readonly OutputTag[];
}

/** Body ของ `POST /v1/output-tags` — สองคีย์ ไม่มีคีย์ที่สาม */
export interface PrintOutputTagBody {
  readonly production_order_id: string;
  readonly operation_seq: number;
}

export interface OutputTagListFilters {
  readonly production_order_id: string;
}
